/**
 * colorModeTransition.js — a troca claro ⇄ escuro como momento, não como piscada.
 *
 * O novo modo "nasce" num círculo que se expande a partir do botão clicado até cobrir a tela
 * (View Transitions API). Quem chama passa `commit`: a função que aplica o modo novo no DOM de forma
 * SÍNCRONA (atributo no <html>, CSS vars, render do React com flushSync) — o navegador fotografa o
 * antes, roda o commit, fotografa o depois e anima entre os dois.
 *
 * Sem suporte à API, ou com "reduzir movimento" ligado no sistema: troca instantânea, sem animação.
 * As regras dos pseudo-elementos `::view-transition-*` estão em src/index.css.
 */

const DURATION_MS = 620;
const EASING = 'cubic-bezier(.22, 1, .36, 1)';

/** Raio que cobre a tela inteira a partir de (x, y): distância até o canto mais distante. */
export function revealRadius(x, y, width, height) {
  return Math.hypot(Math.max(x, width - x), Math.max(y, height - y));
}

/**
 * De onde o círculo nasce: o centro do elemento com foco (o botão de modo que acabou de ser clicado)
 * quando ele está na metade de cima da tela; senão, o canto superior direito, onde o botão mora.
 */
export function revealOrigin(activeRect, viewportWidth, viewportHeight) {
  if (activeRect && activeRect.width > 0 && activeRect.top < viewportHeight / 2) {
    return { x: activeRect.left + activeRect.width / 2, y: activeRect.top + activeRect.height / 2 };
  }
  return { x: Math.max(0, viewportWidth - 120), y: 32 };
}

export function runColorModeTransition(commit) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    commit();
    return;
  }
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (!document.startViewTransition || reduceMotion) {
    commit();
    return;
  }

  const active = document.activeElement;
  const rect = active && active !== document.body ? active.getBoundingClientRect() : null;
  const { x, y } = revealOrigin(rect, window.innerWidth, window.innerHeight);
  const radius = revealRadius(x, y, window.innerWidth, window.innerHeight);

  const transition = document.startViewTransition(commit);
  transition.ready
    .then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        { duration: DURATION_MS, easing: EASING, pseudoElement: '::view-transition-new(root)' },
      );
    })
    .catch(() => { /* transição abortada (ex.: outra troca no meio): o modo já foi aplicado */ });
}
